
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ArtworkModal from './components/ArtworkModal';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Artwork {
  id: number;
  title: string;
  artist: string;
  technique: string;
  category: string;
  size: string;
  price: string;
  year: string;
  materials: string;
  description: string;
  image_url?: string;
  images?: string[];
  created_at: string;
}

export default function Gallery() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [likedItems, setLikedItems] = useState<number[]>([]);
  const [user, setUser] = useState<any>(null);
  const [cartItems, setCartItems] = useState<number[]>([]);
  const [showCartModal, setShowCartModal] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);
  const [showArtworkModal, setShowArtworkModal] = useState(false);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Artwork[]>([]);

  // Mock data as fallback
  const mockArtworks = [
    {
      id: 1,
      title: "Gündoğumu Yansımaları",
      artist: "Mehmet Aksoy",
      technique: "Yağlı Boya",
      category: "oil",
      size: "70x50 cm",
      price: "8500",
      year: "2023",
      materials: "Tuval üzerine yağlı boya",
      description: "Bu eser, doğanın en büyüleyici anlarından biri olan gündoğumunu yansıtır. Sıcak tonlarda hazırlanan bu yağlı boya çalışması, izleyiciyi huzurlu bir sabah atmosferine davet eder. Işığın suya yansıması ve renklerin uyumu, sanatçının doğayla kurduğu derin bağı gösterir.",
      image_url: "https://public.readdy.ai/ai/img_res/749692079314eebeda52282f6a72e896.jpg",
      created_at: "2024-01-01"
    },
    {
      id: 2,
      title: "Sakin Orman",
      artist: "Ayşe Demir",
      technique: "Sulu Boya",
      category: "watercolor",
      size: "60x40 cm",
      price: "4200",
      year: "2024",
      materials: "Sulu boya kağıdı üzerine sulu boya",
      description: "Ormanın derinliklerinde yakalanan bu sessizlik, sulu boyanın şeffaflığı ile mükemmel bir uyum içinde sunulmuştur. Ağaçların arasından süzülen ışık, doğanın sakinliğini ve huzurunu yansıtır.",
      image_url: "https://readdy.ai/api/search-image?query=Peaceful%20forest%20landscape%20watercolor%20painting%2C%20transparent%20watercolor%20technique%20showing%20tall%20trees%20and%20dappled%20sunlight%2C%20soft%20green%20and%20blue%20watercolor%20washes%2C%20delicate%20watercolor%20paper%20texture%2C%20serene%20woodland%20scene%20with%20flowing%20watercolor%20effects&width=400&height=500&seq=gallery2&orientation=portrait",
      created_at: "2024-01-02"
    },
    {
      id: 3,
      title: "Şehir Işıkları",
      artist: "Can Yılmaz",
      technique: "Yağlı Boya",
      category: "oil",
      size: "80x60 cm",
      price: "12000",
      year: "2023",
      materials: "Tuval üzerine yağlı boya",
      description: "Modern şehir yaşamının dinamizmini yansıtan bu eser, gece ışıklarının büyüsünü tuval üzerine taşır. Neon ışıkların yansımaları ve şehrin enerjisi, cesur fırça darbeleriyle hayat bulur.",
      image_url: "https://readdy.ai/api/search-image?query=Urban%20city%20lights%20oil%20painting%20at%20night%2C%20vibrant%20city%20illumination%20with%20colorful%20neon%20reflections%2C%20contemporary%20oil%20painting%20technique%20with%20bold%20brushstrokes%2C%20dynamic%20urban%20landscape%20with%20glowing%20street%20lights%20and%20buildings%2C%20modern%20cityscape%20artwork&width=400&height=500&seq=gallery3&orientation=portrait",
      created_at: "2024-01-03"
    }
  ];

  // Fiyat formatlama fonksiyonu
  const formatPrice = (price: string | number) => {
    // Eğer zaten formatlanmışsa olduğu gibi döndür
    if (typeof price === 'string' && price.includes('TL+KDV')) {
      return price;
    }
    
    // Sayısal değeri al
    const numericPrice = typeof price === 'string' ? parseFloat(price.replace(/[^\d]/g, '')) : price;
    
    // Formatla ve TL+KDV ekle
    return `${numericPrice.toLocaleString('tr-TR')} TL+KDV`;
  };

  useEffect(() => {
    fetchArtworks();
    checkUser();

    // SEO Schema.org JSON-LD for Gallery
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Sanat Galerisi - Nox Sanat",
      "description": "Sanatçılarımızın tüm eserlerini keşfedin. Yağlı boya ve sulu boya tekniklerinde hazırlanmış özgün sanat eserleri.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/gallery`,
      "mainEntity": {
        "@type": "ItemList",
        "name": "Sanat Eserleri Koleksiyonu",
        "description": "Özgün yağlı boya ve sulu boya sanat eserleri koleksiyonu"
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Ana Sayfa",
            "item": import.meta.env.VITE_SITE_URL || "https://example.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Galeri",
            "item": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/gallery`
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (user) {
      fetchUserFavorites();
    }
  }, [user]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = artworks.filter(artwork =>
        artwork.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        artwork.artist.toLowerCase().includes(searchQuery.toLowerCase()) ||
        artwork.technique.toLowerCase().includes(searchQuery.toLowerCase()) ||
        artwork.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, artworks]);

  const fetchArtworks = async () => {
    try {
      const { data, error } = await supabase
        .from('artworks')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // If no artworks in database, use mock data
      setArtworks(data && data.length > 0 ? data : mockArtworks);
    } catch (error) {
      console.error('Error fetching artworks:', error);
      // Fallback to mock data on error
      setArtworks(mockArtworks);
    } finally {
      setIsLoading(false);
    }
  };

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    setUser(session?.user || null);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
      if (session?.user) {
        fetchUserFavorites();
      } else {
        setLikedItems([]);
      }
    });

    return () => subscription.unsubscribe();
  };

  const fetchUserFavorites = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('favorites')
        .select('artwork_id')
        .eq('user_id', user.id);

      if (error) throw error;

      const favoriteIds = data?.map(item => item.artwork_id) || [];
      setLikedItems(favoriteIds);
    } catch (error) {
      console.error('Error fetching user favorites:', error);
    }
  };

  const categories = [
    { id: 'all', name: 'Tüm Eserler', count: artworks.length },
    { id: 'oil', name: 'Yağlı Boya', count: artworks.filter(art => art.category === 'oil').length },
    { id: 'watercolor', name: 'Sulu Boya', count: artworks.filter(art => art.category === 'watercolor').length }
  ];

  const filteredArtworks = selectedCategory === 'all' 
    ? artworks 
    : artworks.filter(artwork => artwork.category === selectedCategory);

  const toggleLike = async (id: number) => {
    if (!user) {
      // Kullanıcı giriş yapmamışsa ana sayfaya yönlendir
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/');
      }
      return;
    }

    const isLiked = likedItems.includes(id);

    try {
      if (isLiked) {
        // Favorilerden kaldır
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('artwork_id', id);

        if (error) throw error;

        setLikedItems(prev => prev.filter(item => item !== id));
      } else {
        // Favorilere ekle
        const { error } = await supabase
          .from('favorites')
          .insert([{ user_id: user.id, artwork_id: id }]);

        if (error) throw error;

        setLikedItems(prev => [...prev, id]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const addToCart = (id: number) => {
    setCartItems(prev => [...prev, id]);
    
    // Show brief feedback with green color
    const button = document.querySelector(`[data-cart-btn="${id}"]`);
    if (button) {
      const originalText = button.textContent;
      const originalClasses = button.className;
      
      // Change to green and show success message
      button.textContent = 'Eklendi!';
      button.className = button.className.replace('bg-white text-black', 'bg-green-500 text-white');
      
      setTimeout(() => {
        button.textContent = originalText;
        button.className = originalClasses;
      }, 2000);
    }
    
    // Close artwork modal if open
    if (showArtworkModal) {
      setShowArtworkModal(false);
    }
  };

  const openArtworkModal = (artwork: Artwork) => {
    setSelectedArtwork(artwork);
    setShowArtworkModal(true);
  };

  const openSearchModal = () => {
    setShowSearchModal(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const navigateToHome = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
    }
  };

  const navigateToAdmin = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/admin');
    }
  };

  const scrollToSection = (sectionId: string) => {
    // Ana sayfaya git ve sonra bölüme kaydır
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300); // Timeout süresini 300ms'ye çıkardım
    }
  };

  const handleAboutClick = () => {
    // Eğer ana sayfada değilsek, önce ana sayfaya git
    const currentPath = window.location.pathname;
    if (currentPath !== '/') {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/');
        setTimeout(() => {
          const aboutSection = document.getElementById('about-section');
          if (aboutSection) {
            aboutSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300); // Daha uzun timeout
      }
    } else {
      // Ana sayfadaysak direkt scroll yap
      const aboutSection = document.getElementById('about-section');
      if (aboutSection) {
        aboutSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleContactClick = () => {
    // Eğer ana sayfada değilsek, önce ana sayfaya git
    const currentPath = window.location.pathname;
    if (currentPath !== '/') {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/');
        setTimeout(() => {
          const contactSection = document.getElementById('contact');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300); // Daha uzun timeout
      }
    } else {
      // Ana sayfadaysak direkt scroll yap
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-300">Eserler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Header 
        cartCount={cartItems.length} 
        onCartClick={() => setShowCartModal(true)}
        onHomeClick={navigateToHome}
        onAboutClick={handleAboutClick}
        onContactClick={handleContactClick}
        onSearchClick={openSearchModal}
      />
      
      {/* Hero Section */}
      <section 
        className="relative pt-24 pb-16 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url('https://readdy.ai/api/search-image?query=Elegant%20art%20gallery%20interior%20with%20multiple%20paintings%20displayed%20on%20walls%2C%20professional%20gallery%20lighting%20illuminating%20artwork%20collection%2C%20sophisticated%20museum%20atmosphere%20with%20visitors%20viewing%20paintings%2C%20contemporary%20gallery%20space%20with%20spotlights%2C%20refined%20art%20exhibition%20environment&width=1920&height=600&seq=galleryhero&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Tam Koleksiyon</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Sanatçılarımızın tüm eserlerini keşfedin. Yağlı boya ve sulu boya tekniklerinde 
            hazırlanmış özgün sanat eserleri sizleri bekyor.
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-white text-black'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-black" data-product-shop>
        <div className="container mx-auto px-6">
          {filteredArtworks.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-image-line text-6xl text-gray-600 mb-4"></i>
              <h3 className="text-2xl font-bold mb-2">Bu kategoride henüz eser bulunmuyor</h3>
              <p className="text-gray-400">Yakında yeni eserler eklenecek</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredArtworks.map((artwork) => (
                <div 
                  key={artwork.id} 
                  className="group bg-gray-900 rounded-lg overflow-hidden hover:bg-gray-800 transition-colors cursor-pointer"
                  onClick={() => openArtworkModal(artwork)}
                >
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img 
                      src={artwork.image_url || "https://readdy.ai/api/search-image?query=Abstract%20art%20painting%20placeholder%2C%20artistic%20background%20with%20paint%20textures%2C%20creative%20artwork%20display%2C%20gallery%20quality%20art%20piece&width=400&height=500&seq=placeholder&orientation=portrait"}
                      alt={artwork.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 flex gap-2">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleLike(artwork.id);
                        }}
                        className={`w-10 h-10 flex items-center justify-center rounded-full backdrop-blur-s

t transition-colors cursor-pointer ${
                          likedItems.includes(artwork.id) 
                            ? 'bg-red-500/80 text-white' 
                            : 'bg-black/50 text-gray-300 hover:text-red-400'
                        }`}
                      >
                        <i className={`ri-heart-${likedItems.includes(artwork.id) ? 'fill' : 'line'} text-lg`}></i>
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          openArtworkModal(artwork);
                        }}
                        className="w-10 h-10 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-full text-gray-300 hover:text-white transition-colors cursor-pointer"
                      >
                        <i className="ri-eye-line text-lg"></i>
                      </button>
                    </div>
                    <div className="absolute bottom-4 left-4">
                      <span className="bg-white/90 text-black text-xs px-3 py-1 rounded-full font-medium">
                        {artwork.technique}
                      </span>
                    </div>
                    {/* Multiple Images Indicator */}
                    {artwork.images && artwork.images.length > 1 && (
                      <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <i className="ri-image-line"></i>
                        {artwork.images.length}
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg font-bold mb-2 group-hover:text-gray-300 transition-colors">
                      {artwork.title}
                    </h3>
                    <p className="text-gray-400 mb-1 text-sm">Sanatçı: {artwork.artist}</p>
                    <p className="text-gray-500 text-xs mb-4">Boyut: {artwork.size}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-white">{formatPrice(artwork.price)}</span>
                      <button 
                        data-cart-btn={artwork.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(artwork.id);
                        }}
                        className="bg-white text-black px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
                      >
                        Sepete Ekle
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Hidden Search Trigger Button */}
      <button 
        data-search-trigger
        onClick={openSearchModal}
        className="hidden"
        aria-hidden="true"
      />

      {/* Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center gap-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Eser adı, sanatçı, teknik veya açıklama ile arayın..."
                    className="w-full px-4 py-3 pl-12 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-white focus:border-transparent"
                    autoFocus
                  />
                  <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
                <button 
                  onClick={() => setShowSearchModal(false)}
                  className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-96">
              {searchQuery.trim() === '' ? (
                <div className="text-center py-12">
                  <i className="ri-search-line text-6xl text-gray-600 mb-4"></i>
                  <p className="text-gray-400 text-lg">Aramaya başlamak için yazmaya başlayın</p>
                  <p className="text-gray-500 text-sm mt-2">Eser adı, sanatçı adı, teknik veya açıklama ile arama yapabilirsiniz</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-search-2-line text-6xl text-gray-600 mb-4"></i>
                  <p className="text-gray-400 text-lg">"{searchQuery}" için sonuç bulunamadı</p>
                  <p className="text-gray-500 text-sm mt-2">Farklı anahtar kelimeler deneyebilirsiniz</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {searchResults.map((artwork) => (
                    <div 
                      key={artwork.id} 
                      className="group bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors cursor-pointer"
                      onClick={() => {
                        setShowSearchModal(false);
                        openArtworkModal(artwork);
                      }}
                    >
                      <div className="relative aspect-[4/5] overflow-hidden">
                        <img 
                          src={artwork.image_url || "https://readdy.ai/api/search-image?query=Abstract%20art%20painting%20placeholder&width=300&height=375&seq=searchplaceholder&orientation=portrait"}
                          alt={artwork.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute bottom-2 left-2">
                          <span className="bg-white/90 text-black text-xs px-2 py-1 rounded-full font-medium">
                            {artwork.technique}
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-4">
                        <h3 className="font-bold mb-1 group-hover:text-gray-300 transition-colors">
                          {artwork.title}
                        </h3>
                        <p className="text-gray-400 text-sm mb-1">{artwork.artist}</p>
                        <p className="text-gray-500 text-xs mb-2">{artwork.size}</p>
                        <p className="font-bold text-white">{formatPrice(artwork.price)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Artwork Detail Modal */}
      <ArtworkModal
        artwork={selectedArtwork}
        isOpen={showArtworkModal}
        onClose={() => setShowArtworkModal(false)}
        onAddToCart={addToCart}
        onToggleLike={toggleLike}
        isLiked={selectedArtwork ? likedItems.includes(selectedArtwork.id) : false}
      />

      {/* Cart Modal */}
      {showCartModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Alışveriş Sepeti</h2>
              <button 
                onClick={() => setShowCartModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-96">
              {cartItems.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-shopping-bag-line text-6xl text-gray-600 mb-4"></i>
                  <p className="text-gray-400 text-lg">Sepetiniz boş</p>
                  <p className="text-gray-500 text-sm mt-2">Galerimizdeki eserleri sepete ekleyerek başlayın</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cartItems.map((itemId) => {
                    const artwork = artworks.find(art => art.id === itemId);
                    if (!artwork) return null;
                    
                    return (
                      <div key={itemId} className="flex items-center gap-4 bg-gray-800 rounded-lg p-4">
                        <img 
                          src={artwork.image_url || "https://readdy.ai/api/search-image?query=Abstract%20art%20painting%20placeholder&width=80&height=100&seq=cartplaceholder&orientation=portrait"}
                          alt={artwork.title}
                          className="w-16 h-20 object-cover object-top rounded"
                        />
                        <div className="flex-1">
                          <h3 className="font-medium">{artwork.title}</h3>
                          <p className="text-gray-400 text-sm">{artwork.artist}</p>
                          <p className="text-gray-500 text-xs">{artwork.size}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatPrice(artwork.price)}</p>
                          <button 
                            onClick={() => setCartItems(prev => prev.filter(item => item !== itemId))}
                            className="text-red-400 hover:text-red-300 transition-colors cursor-pointer"
                          >
                            Kaldır
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {cartItems.length > 0 && (
              <div className="p-6 border-t border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-medium">Toplam:</span>
                  <span className="text-2xl font-bold">
                    {cartItems.reduce((total, itemId) => {
                      const artwork = artworks.find(art => art.id === itemId);
                      if (!artwork) return total;
                      const numericPrice = typeof artwork.price === 'string' ? 
                        parseFloat(artwork.price.replace(/[^\d]/g, '')) : artwork.price;
                      return total + numericPrice;
                    }, 0).toLocaleString('tr-TR')} TL+KDV
                  </span>
                </div>
                <button 
                  onClick={() => {
                    setShowCartModal(false);
                    // Sepet verilerini localStorage'a kaydet
                    localStorage.setItem('cartItems', JSON.stringify(cartItems));
                    if (window.REACT_APP_NAVIGATE) {
                      window.REACT_APP_NAVIGATE('/checkout');
                    }
                  }}
                  className="w-full bg-white text-black py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Ödemeye Geç
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
